import { useCallback, useEffect, useRef, useState } from 'react'

/** State for a single layer in the layer swap system */
export interface LayerState {
  folder: string
  status: 'active' | 'loading' | 'fading-out'
  opacity: number
}

interface UseLayerManagerOptions {
  availableLayers: string[]
  setTilesFolder?: (folder: string) => void
  fadeInDuration?: number
}

export function useLayerManager({
  availableLayers,
  setTilesFolder,
  fadeInDuration = 250,
}: UseLayerManagerOptions) {
  const [layers, setLayers] = useState<LayerState[]>([])
  const [activeLayer, setActiveLayer] = useState<string>('rgb')
  const hasInitialized = useRef(false)
  const animationRef = useRef<number | null>(null)

  // Initialize with first available layer (only once)
  useEffect(() => {
    if (hasInitialized.current || availableLayers.length === 0) return
    hasInitialized.current = true
    const initialLayer = availableLayers.includes('rgb')
      ? 'rgb'
      : availableLayers[0]
    setLayers([{ folder: initialLayer, status: 'active', opacity: 1 }])
    setActiveLayer(initialLayer)
    setTilesFolder?.(initialLayer)
  }, [availableLayers, setTilesFolder])

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])

  // Animate fade-in of new layer
  const startFadeIn = useCallback(
    (newLayerFolder: string) => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }

      const startTime = performance.now()

      const animate = () => {
        const elapsed = performance.now() - startTime
        const progress = Math.min(elapsed / fadeInDuration, 1)
        const eased = 1 - (1 - progress) * (1 - progress)

        setLayers((prevLayers) =>
          prevLayers.map((layer) => {
            if (layer.folder === newLayerFolder) {
              return { ...layer, opacity: eased }
            }
            return layer
          })
        )

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate)
        } else {
          setLayers((prevLayers) =>
            prevLayers.map((l) =>
              l.folder === newLayerFolder
                ? { ...l, status: 'active' as const, opacity: 1 }
                : l
            )
          )
          animationRef.current = null
        }
      }

      animationRef.current = requestAnimationFrame(animate)
    },
    [fadeInDuration]
  )

  // Handle layer change request
  const handleLayerChange = useCallback(
    (newLayer: string) => {
      if (newLayer === activeLayer) return
      if (layers.some((l) => l.folder === newLayer)) return

      setLayers((prevLayers) => [
        ...prevLayers,
        { folder: newLayer, status: 'loading' as const, opacity: 0 },
      ])
      setActiveLayer(newLayer)
      setTilesFolder?.(newLayer)
    },
    [activeLayer, layers, setTilesFolder]
  )

  // Handle when a new layer finishes loading
  const handleLayerLoaded = useCallback(
    (folder: string) => {
      setLayers([{ folder, status: 'active' as const, opacity: 0 }])
      startFadeIn(folder)
    },
    [startFadeIn]
  )

  return {
    layers,
    activeLayer,
    loadingLayer: layers.find((l) => l.status === 'loading')?.folder,
    handleLayerChange,
    handleLayerLoaded,
  }
}
